/**
 * Saved views over the regional eval API (design doc 033 slice 6).
 *
 * A view is a lens over *this region's* evaluation data — its slice names
 * metadata keys, values and criteria that exist because this stamp produced
 * them — so it lives beside the data it filters rather than in the Global
 * Layer. The same page already calls this service for every aggregate,
 * metadata key and criterion.
 *
 * The transport carries the Cognito bearer token and `X-Impersonate-Domain`
 * already, so a staff session viewing a customer reads that customer's views
 * with nothing extra here.
 *
 * The server owns `domain` and `owner`; neither is ever sent. It also refuses
 * writes while impersonating, which is why the strip hides its save and delete
 * controls in that state rather than letting a request fail.
 */
import { makeApiRequestPromise } from "../../../lib/api";
import type { PersistedFilterState } from "../utils/filterPersistence";
import type { SavedView } from "../utils/savedViews";

const PATH = "/api/v1/saved-views";

/** The wire shape: the slice is opaque to the server. */
interface RemoteView {
  id: string;
  name: string;
  owner: string;
  slice: PersistedFilterState;
}

const toSavedView = (remote: RemoteView): SavedView => ({
  ...remote.slice,
  filters: remote.slice?.filters ?? [],
  id: remote.id,
  name: remote.name,
  owner: remote.owner,
});

export async function fetchSavedViews(namespace: string): Promise<SavedView[]> {
  const remote: RemoteView[] = await makeApiRequestPromise({
    path: PATH,
    method: "GET",
    params: { namespace },
  });
  return (remote ?? []).map(toSavedView);
}

export async function createSavedView(
  namespace: string,
  view: { name: string; slice: PersistedFilterState },
): Promise<SavedView> {
  const remote: RemoteView = await makeApiRequestPromise({
    path: PATH,
    method: "POST",
    body: {
      namespace,
      name: view.name,
      slice: view.slice,
    },
  });
  return toSavedView(remote);
}

export async function updateSavedView(
  namespace: string,
  id: string,
  view: { name: string; slice: PersistedFilterState },
): Promise<SavedView> {
  const remote: RemoteView = await makeApiRequestPromise({
    path: `${PATH}/${id}`,
    method: "PUT",
    body: {
      namespace,
      name: view.name,
      slice: view.slice,
    },
  });
  return toSavedView(remote);
}

export async function deleteSavedView(id: string): Promise<void> {
  await makeApiRequestPromise({ path: `${PATH}/${id}`, method: "DELETE" });
}
